import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { relativeTimeLabel } from "../lib/relativeTime";
import { MainCard } from "../components/MainCard";

const ADMIN_KEY_ACTOR = "admin-key";

/**
 * Audit tab (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md Part B) — surfaces the append-only
 * moderation/admin action log (docs/105-ADMIN-MODERATION-AND-MEMBERSHIP-ENFORCEMENT.md v0
 * `AuditEvent`) that `adminAudit()` already shipped in `BackendClient` with **zero mantis
 * callers** (docs/340 §2.1's "unused method" table) — no new worker endpoint, `GET
 * /admin/audit` already exists and already sorts newest-first server-side. A per-row actor/
 * target column resolves the raw user id to an email via the same `adminUsers()` +
 * `emailById` lookup `NoticesPage`/`CatalogsPage` already use; `AuditEvent.at` is epoch
 * SECONDS (`worker/src/index.ts`'s `Deps.now()`), so every timestamp render below does
 * `* 1000` (the same unit fix as docs/340's manifest-timestamp defect, §3).
 */
export function AuditPage() {
  const { client, token } = useAuth();
  const [query, setQuery] = useState("");

  const auditQuery = useQuery({
    queryKey: ["admin-audit"],
    queryFn: () => client!.adminAudit(token!),
    enabled: !!client && !!token,
  });
  const usersQuery = useQuery({
    queryKey: ["admin-users", false],
    queryFn: () => client!.adminUsers(token!),
    enabled: !!client && !!token,
  });

  const events = auditQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const emailById = useMemo(() => new Map(users.map((u) => [u.id, u.email])), [users]);
  const labelFor = (id: string) => (id === ADMIN_KEY_ACTOR ? "admin key" : (emailById.get(id) ?? id));

  const ql = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!ql) return events;
    return events.filter((e) => {
      const haystack = [
        e.action,
        labelFor(e.actorId),
        e.targetUserId ? labelFor(e.targetUserId) : "",
        e.targetRef ?? "",
        e.reason ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(ql);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, ql, emailById]);

  const now = Date.now();

  return (
    <MainCard title="Audit log">
      {auditQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(auditQuery.error as Error).message}
        </Alert>
      ) : null}
      <TextField
        size="small"
        fullWidth
        placeholder="Search by actor, action, target, or reason…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
      />
      {auditQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          {events.length === 0 ? "No audit events yet." : "No events match your search."}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.action}</TableCell>
                  <TableCell>{labelFor(e.actorId)}</TableCell>
                  <TableCell>
                    {e.targetUserId ? labelFor(e.targetUserId) : null}
                    {e.targetUserId && e.targetRef ? " · " : null}
                    {e.targetRef ? (
                      <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {e.targetRef}
                      </Typography>
                    ) : null}
                    {!e.targetUserId && !e.targetRef ? "—" : null}
                  </TableCell>
                  <TableCell>{e.reason ?? "—"}</TableCell>
                  <TableCell>
                    <Tooltip title={new Date(e.at * 1000).toLocaleString()}>
                      <span>{relativeTimeLabel(now, e.at * 1000)}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </MainCard>
  );
}
